import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatLongDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import { downloadProtectedFile } from "@/lib/fileAccess";
import { FileText, Plus, Download } from "lucide-react";

const STATUS_COLORS = {
  Pending: "bg-amber-100 text-amber-700 border-amber-200",
  Approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Rejected: "bg-rose-100 text-rose-700 border-rose-200",
};

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

function currentFY() {
  const d = new Date();
  const y = d.getFullYear();
  return d.getMonth() >= 3 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

const emptyDecl = {
  financial_year: currentFY(),
  section_80c: "",
  section_80d: "",
  section_80e: "",
  hra: "",
  home_loan_interest: "",
  other_deductions: "",
  notes: "",
};

export default function MyTax() {
  const [tab, setTab] = useState("form16");
  const [form16, setForm16] = useState([]);
  const [decls, setDecls] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyDecl);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/me/tax/form16").catch(() => ({ data: [] })),
      api.get("/me/tax/declarations").catch(() => ({ data: [] })),
    ]);
    setForm16(a.data || []);
    setDecls(b.data || []);
  };

  useEffect(() => {
    load();
  }, []);

  const fyOptions = useMemo(() => {
    const cur = currentFY();
    const [a] = cur.split("-").map(Number);
    return [`${a - 1}-${a}`, cur, `${a + 1}-${a + 2}`];
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      await api.post("/me/tax/declarations", {
        financial_year: form.financial_year,
        section_80c: Number(form.section_80c || 0),
        section_80d: Number(form.section_80d || 0),
        section_80e: Number(form.section_80e || 0),
        hra: Number(form.hra || 0),
        home_loan_interest: Number(form.home_loan_interest || 0),
        other_deductions: Number(form.other_deductions || 0),
        notes: form.notes,
      });
      setMsg("Tax declaration (12BB) submitted for review.");
      setOpen(false);
      setForm({ ...emptyDecl, financial_year: form.financial_year });
      await load();
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  const money = (n) =>
    Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

  return (
    <AdminLayout title="Form 16 / Tax" breadcrumbs={["Home", "Self Service", "Tax"]}>
      {err && (
        <div className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>
      )}
      {msg && (
        <div className="mb-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">{msg}</div>
      )}

      <div className="flex gap-1 border-b border-slate-200 mb-4">
        {[
          { id: "form16", label: "Form 16" },
          { id: "12bb", label: "Tax declaration (12BB)" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id
                ? "border-[#226DB4] text-[#226DB4]"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "form16" && (
        <div className="bg-white border border-slate-200 rounded shadow-sm">
          <div className="px-4 py-2.5 border-b border-slate-200 flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#226DB4]" />
            <h3 className="text-sm font-semibold text-slate-800">Your Form 16</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Financial year</th>
                <th className="text-left px-4 py-2.5 font-semibold">File</th>
                <th className="text-left px-4 py-2.5 font-semibold">Uploaded</th>
                <th className="text-right px-4 py-2.5 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {form16.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-slate-400">
                    No Form 16 uploaded yet. Payroll / HR will publish it here when ready.
                  </td>
                </tr>
              ) : form16.map((f) => (
                <tr key={f.id}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{f.financial_year}</td>
                  <td className="px-4 py-2.5 text-slate-600">{f.file_name}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">
                    {formatLongDate(f.uploaded_at)}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => downloadProtectedFile(f.download_url || `/api/me/tax/form16/${f.id}/download`, f.file_name)}
                      className="inline-flex items-center gap-1 text-xs font-medium text-[#226DB4] hover:underline"
                    >
                      <Download className="h-3.5 w-3.5" /> Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "12bb" && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm text-slate-500">
              Declare proposed tax investments (Form 12BB). HR / Payroll will review amounts.
            </p>
            <button
              type="button"
              onClick={() => { setOpen(true); setErr(""); setMsg(""); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded bg-[#226DB4] hover:bg-[#1B5A94] text-white"
            >
              <Plus className="h-4 w-4" /> New declaration
            </button>
          </div>

          {open && (
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded shadow-sm p-5 mb-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">Submit 12BB declaration</h3>
              <label className="block text-sm max-w-xs">
                <span className="text-xs font-semibold text-slate-500 uppercase">Financial year</span>
                <select className={inputCls} value={form.financial_year}
                  onChange={(e) => setForm({ ...form, financial_year: e.target.value })}>
                  {fyOptions.map((fy) => <option key={fy} value={fy}>{fy}</option>)}
                </select>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  ["section_80c", "Section 80C"],
                  ["section_80d", "Section 80D (medical)"],
                  ["section_80e", "Section 80E (education loan)"],
                  ["hra", "HRA exemption"],
                  ["home_loan_interest", "Home loan interest (24b)"],
                  ["other_deductions", "Other deductions"],
                ].map(([key, label]) => (
                  <label key={key} className="block text-sm">
                    <span className="text-xs font-semibold text-slate-500 uppercase">{label}</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      className={inputCls}
                      value={form[key]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      placeholder="0"
                    />
                  </label>
                ))}
              </div>
              <label className="block text-sm">
                <span className="text-xs font-semibold text-slate-500 uppercase">Notes</span>
                <textarea rows={2} className={inputCls} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Optional details for HR" />
              </label>
              <div className="flex gap-2">
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm font-medium rounded bg-[#089949] hover:bg-[#067A3A] text-white disabled:opacity-60">
                  {saving ? "Submitting…" : "Submit"}
                </button>
                <button type="button" onClick={() => setOpen(false)}
                  className="px-4 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </form>
          )}

          <div className="bg-white border border-slate-200 rounded shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold">FY</th>
                  <th className="text-right px-4 py-2.5 font-semibold">80C</th>
                  <th className="text-right px-4 py-2.5 font-semibold">80D</th>
                  <th className="text-right px-4 py-2.5 font-semibold">HRA</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Home loan</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {decls.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-slate-400">
                      No declarations submitted yet
                    </td>
                  </tr>
                ) : decls.map((d) => (
                  <tr key={d.id}>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{d.financial_year}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">₹{money(d.section_80c)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">₹{money(d.section_80d)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">₹{money(d.hra)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">₹{money(d.home_loan_interest)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs border ${STATUS_COLORS[d.status] || ""}`}>
                        {d.status}
                      </span>
                      {d.decision_note && (
                        <div className="text-xs text-slate-400 mt-0.5">{d.decision_note}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
